import PropTypes from 'prop-types';
import { createContext, useContext, useMemo } from 'react';
import { UserListContext } from './UserListContext';

const EmployeeListContext = createContext({
  employees: [],
  employeesById: {},
  employeesByUsername: {},
  employeesQueryA: [],
  employeesByIdQueryA: {},
  isLoadingEmployees: false,
});

const EmployeeListContextProvider = ({ children }) => {
  const { users, isLoadingUsers } = useContext(UserListContext);

  const employees = useMemo(() => {
    const filtered = users.filter((u) => u.is_employee);
    return filtered.sort((a, b) =>
      (a.full_name || '').localeCompare(b.full_name || '')
    );
  }, [users]);

  const employeesById = useMemo(
    () =>
      Object.freeze(
        Object.fromEntries(employees.map((employee) => [employee.id, employee]))
      ),
    [employees]
  );

  const employeesByUsername = useMemo(
    () =>
      Object.freeze(
        Object.fromEntries(
          employees.map((employee) => [employee.username, employee])
        )
      ),
    [employees]
  );

  // Query A: dipendenti attivi (sort full_name) + dipendenti licenziati (sort outgoing_date desc).
  // Anche i licenziati devono avere il ruolo employees: chi non era dipendente non appare.
  const employeesQueryA = useMemo(() => {
    const active = users
      .filter(u => u.is_employee && !u.outgoing_date)
      .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
    const terminated = users
      .filter(u => u.is_employee && u.outgoing_date)
      .sort((a, b) => (b.outgoing_date || '').localeCompare(a.outgoing_date || ''));
    return [...active, ...terminated];
  }, [users]);

  const employeesByIdQueryA = useMemo(
    () => Object.freeze(Object.fromEntries(employeesQueryA.map(e => [e.id, e]))),
    [employeesQueryA]
  );

  return (
    <EmployeeListContext.Provider
      value={{
        employees,
        employeesById,
        employeesByUsername,
        employeesQueryA,
        employeesByIdQueryA,
        isLoadingEmployees: isLoadingUsers,
      }}
    >
      {children}
    </EmployeeListContext.Provider>
  );
};

EmployeeListContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { EmployeeListContext, EmployeeListContextProvider };
